/**
 * Typed API client for the Event Bus (epic 201).
 *
 * Tenant admins subscribe HTTP handlers to domain-event patterns (globs like
 * `lead.*` or `*`). Each subscription is signed with an HMAC secret returned
 * once at creation (reveal-once banner). Per-subscription delivery history is
 * read-only.
 */

import { apiClient } from '@/lib/apiClient';

export interface EventSubscriptionRow {
  id: string;
  oauthClientId: string | null;
  eventPattern: string;
  handlerUrl: string;
  retryMaxAttempts: number;
  enabled: boolean;
  /**
   * True when this subscriber is a data PROCESSOR that must acknowledge privacy
   * operations (`OnPrivacyErasure` / `OnPrivacyExport`), not merely observe them
   * (VYV-K7.5, ADR-031). Until it acknowledges, the erasure/export is NOT
   * complete. Default false: a webhook never acquires a regulatory duty by
   * accident.
   */
  privacyParticipant: boolean;
  createdAt: string;
}

export interface CreateSubscriptionRequest {
  eventPattern: string;
  handlerUrl: string;
  oauthClientId?: string | null;
  retryMaxAttempts?: number | null;
  privacyParticipant?: boolean;
}

export interface UpdateSubscriptionRequest {
  eventPattern?: string;
  handlerUrl?: string;
  retryMaxAttempts?: number;
  enabled?: boolean;
  privacyParticipant?: boolean;
}

/** `secret` is the HMAC signing secret — shown once at creation. */
export interface CreatedSubscription {
  id: string;
  secret: string;
}

export interface DeliveryRow {
  id: string;
  eventName: string;
  eventId: string;
  status: string;
  attempt: number;
  nextRetryAt: string | null;
  lastError: string | null;
  responseStatus: number | null;
  deliveredAt: string | null;
  createdAt: string;
}

/** Catalog of emitted event names (e.g. "lead.created") — used as hints. */
export async function listEventCatalog(): Promise<string[]> {
  const { data } = await apiClient.get<string[]>('/events/catalog');
  return data;
}

export async function listSubscriptions(): Promise<EventSubscriptionRow[]> {
  const { data } = await apiClient.get<EventSubscriptionRow[]>('/events/subscriptions');
  return data;
}

export async function createSubscription(
  req: CreateSubscriptionRequest,
): Promise<CreatedSubscription> {
  const { data } = await apiClient.post<CreatedSubscription>('/events/subscriptions', req);
  return data;
}

export async function updateSubscription(
  id: string,
  req: UpdateSubscriptionRequest,
): Promise<EventSubscriptionRow> {
  const { data } = await apiClient.patch<EventSubscriptionRow>(
    `/events/subscriptions/${id}`,
    req,
  );
  return data;
}

export async function deleteSubscription(id: string): Promise<void> {
  await apiClient.delete(`/events/subscriptions/${id}`);
}

export async function listDeliveries(subscriptionId: string): Promise<DeliveryRow[]> {
  const { data } = await apiClient.get<DeliveryRow[]>(
    `/events/subscriptions/${subscriptionId}/deliveries`,
  );
  return data;
}
